import _ from 'lodash'
import moment from 'moment'
import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/database'
import { unassignedClientId } from './constants'
import { forEachPromise } from './utils'
import { billingFrequencies } from '@evergreen/shared/constants'
import toast from './components/Toast'

const ota = big => _.map(big, (o, id) => ({ ...o, id })) //object to array

let config = {
  apiKey: 'AIzaSyARPHPwy-sQn7IMymBbW_q9R-Chsf-uIcg',
  authDomain: 'evergreen-350bb.firebaseapp.com',
  databaseURL: 'https://evergreen-350bb.firebaseio.com',
  projectId: 'evergreen-350bb',
  storageBucket: 'evergreen-350bb.appspot.com',
  messagingSenderId: '405032315264',
}

if (!firebase.apps.length) {
  // cuz of fast reload with create-react-app.  Otherwise you get this error:  "Firebase App named '[DEFAULT]' already exists (app/duplicate-app)."
  firebase.initializeApp(config)
}
let dbRef = () => firebase.database().ref()

window.dbRef = dbRef
window.firebase = firebase

export async function addLead({ month, clientId = unassignedClientId }) {
  let leadId = `${clientId}_${Date.now()}`

  try {
    await dbRef().child(`/leads/${month}/${clientId}/${leadId}`).set({
      date: Date.now(),
      status: 'uncontacted',
    })
  } catch (e) {
    console.log('error adding lead!', e)
    alert('Are you offline?  The lead may not have been added properly')
  }
}

export async function updateToSent({
  month,
  clientId,
  leadId,
  leadNote,
  sentDate,
  ...leadProps
}) {
  try {
    let currentMonth = moment().format('YYYY-MM')

    if (currentMonth !== month) {
      window.alert(
        'The "Sent" date\'s month is different from the month the lead was received.  The lead will now be updated to the current month.'
      )
      await dbRef().child(`/leads/${month}/${clientId}/${leadId}`).remove()
      await dbRef().child(`/lead-notes/${month}/${clientId}/${leadId}`).remove()
      await dbRef()
        .child(`/lead-notes/${currentMonth}/${clientId}/${leadId}`)
        .set(leadNote)
    }

    await dbRef()
      .child(`/leads/${currentMonth}/${clientId}/${leadId}`)
      .set({
        ...leadProps,
        sentDate,
        status: 'sent',
      })
  } catch (e) {
    console.log('error updating status to "sent"!', e)
    alert(
      'Are you offline?  The lead field may not have been updated properly or you do not have permission to update this field'
    )
  }
}

export async function updateLeadField({
  month,
  clientId,
  leadId,
  field,
  value,
}) {
  try {
    await dbRef()
      .child(`/leads/${month}/${clientId}/${leadId}/${field}`)
      .set(value)
  } catch (e) {
    console.log('error updating lead field!', e)
    alert(
      'Are you offline?  The lead field may not have been updated properly or you do not have permission to update this field'
    )
  }
}

export async function assignLeadToNewClient({
  month,
  newClientId,
  lead,
  leadNote,
}) {
  let { leadId, clientId, ...leadProps } = lead
  leadProps.date = leadProps.date || Date.now()
  leadProps.billingType = null // cuz it was looked up but don't need to save it
  let newLeadId = `${newClientId}_${leadProps.date}`

  try {
    await dbRef().child(`/leads/${month}/${clientId}/${leadId}`).remove()
    await dbRef()
      .child(`/leads/${month}/${newClientId}/${newLeadId}`)
      .set(leadProps)
    await dbRef().child(`/lead-notes/${month}/${clientId}/${leadId}`).remove()
    await dbRef()
      .child(`/lead-notes/${month}/${newClientId}/${newLeadId}`)
      .set(leadNote)
  } catch (e) {
    console.log('error updating lead field!', e)
    alert('Are you offline?  The lead field may not have been updated properly')
  }
}

export const deleteLead = async ({ month, clientId, leadId }) => {
  await dbRef().child(`/leads/${month}/${clientId}/${leadId}`).remove()
  await dbRef().child(`/lead-notes/${month}/${clientId}/${leadId}`).remove()
}

export async function updateLeadNote({ month, clientId, leadId, value }) {
  try {
    await dbRef().child(`/lead-notes/${month}/${clientId}/${leadId}`).set(value)
  } catch (e) {
    console.log('error updating lead note!', e)
    alert('Are you offline?  The lead note may not have been updated properly')
  }
}

export async function saveClient({ oldClient = {}, client }) {
  if (_.isEmpty(oldClient)) {
    let clientExists = await dbRef()
      .child(`/clients/${client.id}`)
      .once('value')
      .then(s => !_.isEmpty(s.val()))

    if (clientExists) {
      throw Error(`A client already exists with the ID "${client.id}"`)
    }
  }
  await dbRef()
    .child(`/clients/${client.id}`)
    .set({
      ..._.omit(client, ['id']),
      cityDropdownOptions: !client.cityDropdownOptions
        ? ''
        : _.chain(client.cityDropdownOptions)
            .split('\n')
            .map(_.trim)
            .uniq()
            .sortBy()
            .compact()
            .join('\n')
            .value(),
    })

  await forEachPromise(
    _.map(client.users, (user, userId) => ({ ...user, userId })),
    user =>
      dbRef()
        .child(`/users/${user.userId}`)
        .update({
          ...user,
          clientId: client.id,
        })
  )

  let mappingUpdateCount = _.max([
    _.compact(oldClient.parseMappings).length,
    _.compact(client.parseMappings).length,
  ])
  let parseMappingUpdates = _.transform(
    _.times(mappingUpdateCount),
    (acc, index) => {
      acc[`${client.id}_index_${index}`] = !client.parseMappings[index]
        ? null
        : { ...client.parseMappings[index], clientId: client.id }
    },
    {}
  )

  await dbRef().child(`/parse-mappings`).update(parseMappingUpdates)

  if (client.billingClientId !== client.id) {
    let parentUserIds = await dbRef()
      .child('/users')
      .orderByChild('clientId')
      .equalTo(client.billingClientId)
      .once('value')
      .then(s => _.keys(s.val()))

    await forEachPromise(parentUserIds, async uid => {
      await dbRef()
        .child(`/users/${uid}/billingClientIds/${client.id}`)
        .set(true)
    })

    if (client.billingClientId !== oldClient.billingClientId) {
      let oldParentUserIds = await dbRef()
        .child('/users')
        .orderByChild('clientId')
        .equalTo(oldClient.billingClientId)
        .once('value')
        .then(s => _.keys(s.val()))

      await forEachPromise(oldParentUserIds, async uid => {
        await dbRef()
          .child(`/users/${uid}/billingClientIds/${client.id}`)
          .set(null)
      })
    }
  }
}

export const savePartnerClient = async client => {
  await forEachPromise(
    _.map(client.users, (user, userId) => ({ ...user, userId })),
    user =>
      dbRef()
        .child(`/users/${user.userId}`)
        .update({
          ...user,
          clientId: client.id,
        })
  )
}

export const watchLeadNotes = (month, callback) => {
  let ref = dbRef().child(`/lead-notes/${month}`)
  let cb = s => callback(s.val())

  ref.on('value', cb)

  return () => ref.off('value', cb)
}

export const watchBillingHistory = callback => {
  let ref = dbRef().child(`/billing-history`)
  let cb = s => callback(ota(s.val()))

  ref.on('value', cb)

  return () => ref.off('value', cb)
}

export const watchClients = callback => {
  let ref = dbRef().child('clients')
  let cb = s => callback(ota(s.val()))

  ref.on('value', cb, err => {
    console.error(err)
    toast.error('There was an error loading clients', {
      autoClose: false,
    })
  })

  return () => ref.off('value', cb)
}

export const watchBillingClients = (clientId, callback) => {
  let ref = dbRef()
    .child('clients')
    .orderByChild('billingClientId')
    .equalTo(clientId)
  let cb = s => callback(ota(s.val()))
  ref.on('value', cb, err => {
    console.log(err)
    toast.error(`There was an error accessing billing clients`, {
      autoClose: false,
    })
  })

  return () => ref.off('value', cb)
}

export const watchUserClient = (clientId, callback) => {
  let ref = dbRef().child('clients').child(clientId)
  let cb = s => callback({ id: clientId, ...s.val() })
  ref.on('value', cb, err => {
    console.log(err)
    toast.error(`There was an error accessing clientId ${clientId}`, {
      autoClose: false,
    })
  })

  return () => ref.off('value', cb)
}

export const watchPartnerClients = ({ partnerClientIds }, callback) => {
  let removeListeners = _.map(partnerClientIds, clientId => {
    let ref = dbRef().child(`/clients/${clientId}`)
    let cb = s => {
      callback({ id: clientId, ...s.val() })
    }

    ref.on('value', cb, err => {
      console.error(err)
      toast.error(`There was an error loading leads for ${clientId}`, {
        autoClose: false,
      })
    })
    return () => ref.off('value', cb)
  })

  return () => _.forEach(removeListeners, remove => remove())
}

export const savePartner = async ({ oldPartner = {}, partner }) => {
  let { id: partnerId, ...propsForSave } = partner
  let partnerRef = dbRef().child(`/partners/${partnerId}`)
  if (_.isEmpty(oldPartner)) {
    let partnerExists = await partnerRef
      .once('value')
      .then(s => !_.isEmpty(s.val()))

    if (partnerExists) {
      throw Error(`A partner already exists with the ID "${partnerId}"`)
    }
  }

  let oldClientIdsList = _.keys(oldPartner.clientIds)
  let clientIdsList = _.keys(partner.clientIds)

  await partnerRef.set(propsForSave)
  await forEachPromise(
    _.map(partner.users, (user, userId) => ({ ...user, userId })),
    user =>
      dbRef()
        .child(`/users/${user.userId}`)
        .update({ ...user, partnerId, partnerClientIds: partner.clientIds })
  )

  // if removing a clientId from partner, be sure to update client to remove partner key
  let allClientIds = _.union(oldClientIdsList, clientIdsList)

  await forEachPromise(
    allClientIds,
    clientId =>
      dbRef()
        .child(`/clients/${clientId}`)
        .update({
          [`partnerIds/${partnerId}`]: _.includes(clientIdsList, clientId)
            ? true
            : null,
        }) // do null for when client ID is removed so it gets deleted from db
  )
}

export const watchPartners = callback => {
  let ref = dbRef().child('partners')
  let cb = s => callback(ota(s.val()))

  ref.on('value', cb)

  return () => ref.off('value', cb)
}

export const watchLeads = (month, callback) => {
  let ref = dbRef().child(`/leads/${month}`)
  let cb = s => {
    let arrayOfLeads = _.transform(
      s.val(),
      (acc, clientLeads, clientId) => {
        _.each(clientLeads, (lead, leadId) => {
          acc.push({
            clientId,
            leadId,
            ...lead,
          })
        })
      },
      []
    )

    callback(arrayOfLeads)
  }

  ref.on('value', cb)

  return () => ref.off('value', cb)
}

export const watchPartnerLeads = ({ month, partnerClientIds }, callback) => {
  let removeListeners = _.map(partnerClientIds, clientId => {
    let ref = dbRef().child(`/leads/${month}/${clientId}`)
    let cb = s => {
      callback({
        clientId,
        clientLeads: _.map(s.val(), (lead, leadId) => ({
          ...lead,
          leadId,
          clientId,
        })),
      })
    }

    ref.on('value', cb, err => {
      console.error(err)
      toast.error(`There was an error loading clients for ${clientId}`, {
        autoClose: false,
      })
    })
    return () => ref.off('value', cb)
  })

  return () => _.forEach(removeListeners, remove => remove())
}

export const watchPartnerLeadNotes = (
  { month, partnerClientIds },
  callback
) => {
  let removeListeners = _.map(partnerClientIds, clientId => {
    let ref = dbRef().child(`/lead-notes/${month}/${clientId}`)
    let cb = snap => callback({ [clientId]: snap.val() })

    ref.on('value', cb, err => {
      console.error(err)
      toast.error(`There was an error loading lead notes for ${clientId}`, {
        autoClose: false,
      })
    })
    return () => ref.off('value', cb)
  })

  return () => _.forEach(removeListeners, remove => remove())
}

export const getClientIdsToBeBilled = (
  billingFrequency = billingFrequencies.semimonthly
) =>
  dbRef()
    .child(`/clients`)
    .orderByChild('billingType')
    .equalTo('perLead')
    .once('value')
    .then(s =>
      _(s.val())
        .filter(client => client.billingFrequency === billingFrequency)
        .map('billingClientId')
        .uniq()
        .value()
    )

export const watchClientLeads = ({ month, leadClientIds }, callback) => {
  let removeListeners = _.map(leadClientIds, clientId => {
    let ref = dbRef().child(`/leads/${month}/${clientId}`)
    let cb = s => {
      let arrayOfLeads = _.transform(
        s.val(),
        (acc, lead, leadId) => {
          acc.push({ leadClientId: clientId, leadId, ...lead })
        },
        []
      )
      callback(arrayOfLeads)
    }

    ref.on('value', cb, err => {
      console.error(err)
      toast.error(`There was an error loading leads for ${clientId}`, {
        autoClose: false,
      })
    })
    return () => ref.off('value', cb)
  })

  return removeListeners
}

export const watchClientLeadNotes = ({ month, leadClientIds }, callback) => {
  let removeListeners = _.map(leadClientIds, clientId => {
    let ref = dbRef().child(`/lead-notes/${month}/${clientId}`)
    let cb = s => {
      callback({ [clientId]: s.val() })
    }

    ref.on('value', cb, err => {
      console.error(err)
      toast.error(`There was an error loading leads for ${clientId}`, {
        autoClose: false,
      })
    })
    return () => ref.off('value', cb)
  })

  return removeListeners
}

let oldLeadsCache = {}

export const getOlderLeads = async ({ month, leadClientIds }) => {
  // load all leads client side for date range search, but cache the old ones
  if (oldLeadsCache[month]) return oldLeadsCache[month]
  let allClientOldLeads = []
  await forEachPromise(leadClientIds, async clientId => {
    try {
      let snap = await dbRef()
        .child(`/leads/${month}/${clientId}`)
        .once('value')
      let oldLeads = _.transform(
        snap.val(),
        (acc, lead, leadId) => {
          acc.push({ leadClientId: clientId, leadId, ...lead })
        },
        []
      )
      allClientOldLeads = allClientOldLeads.concat(oldLeads)
    } catch (e) {
      console.error(e)
      toast.error(`Error getting leads for ${clientId}`, { autoClose: false })
    }
  })

  oldLeadsCache[month] = allClientOldLeads

  return allClientOldLeads
}

let oldNotesCache = {}
export const getOlderNotes = async ({ month, leadClientIds }) => {
  if (oldNotesCache[month]) return oldNotesCache[month]
  let allClientOldNotes = {}
  await forEachPromise(leadClientIds, async clientId => {
    try {
      let snap = await dbRef()
        .child(`/lead-notes/${month}/${clientId}`)
        .once('value')
      let oldNotes = { [clientId]: snap.val() || {} } // default to {} cuz if null, _.merge will cause null to overwrite
      allClientOldNotes = { ...allClientOldNotes, ...oldNotes }
    } catch (e) {
      console.error(e)
      toast.error(`Error getting leads for ${clientId}`, { autoClose: false })
    }
  })

  oldNotesCache[month] = allClientOldNotes

  return allClientOldNotes
}

export const watchDueDiligenceHistory = callback => {
  let ref = dbRef().child('/due-diligence-history')
  let cb = s => callback(ota(s.val()))

  ref.on('value', cb)

  return () => ref.off('value', cb)
}

export const saveDueDiligenceHistory = async data =>
  dbRef()
    .child('/due-diligence-history')
    .push({ dateCreated: Date.now(), ...JSON.parse(JSON.stringify(data)) })

export const deleteDueDiligenceHistory = async docId =>
  dbRef().child(`/due-diligence-history/${docId}`).remove()
